import { Check } from 'lucide-react';
import { useTranslation } from '../../../../lib/i18n';
import { useProductName } from '../../../../lib/use-brand';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { useItemSeenBi } from '../../../../lib/use-item-seen-bi';
import { CREDITS_WIDGET_FIELD, creditsCardWidgetType } from '../../../../lib/credits-widget-bi';
import { creditsCardFraction, type CreditsCardData } from '../../../../lib/credits-card';
import { Button, Card, ProgressBar, Typography } from '../../ui';
import styles from './CreditsCard.module.css';
import { upgradeHref } from '../../../../lib/upgrade-origin';
import { useFreeUsageUntil } from '../../../../lib/use-free-usage';

/**
 * The "running low on credits" card, rendered UNDER an assistant reply that carries
 * `metadata.creditsCard` — a sibling of `ConnectorConnectCard`, which rides its reply the same way.
 *
 * **Deliberately not a modal.** A prompt that covers the screen at the moment something worked
 * interrupts the very thing it is congratulating, and a user mid-task has to dismiss it before they
 * can carry on. In the thread it is part of the conversation, it keeps its context (the success line
 * sits right above the answer it refers to), and it costs nothing to scroll past.
 *
 * **The primary action depends on the variant, and the inversion is the point.**
 *
 * On `low`, upgrade leads: this card reaches a user who has NOT been blocked yet, so leading with the
 * free path would trade paid conversion for credits we give away — and this is precisely the
 * population where that trade is invisible without a held-out arm.
 *
 * On `renewed`, earning leads. That user already answered the upgrade question: they hit zero, and
 * they waited out an entire cycle rather than pay. Opening with the same ask ignores the clearest
 * signal a user can give that price is the obstacle, so the free path goes first and upgrade stays
 * available beside it.
 */
export function CreditsCard({
  card,
  onDismiss,
}: {
  card: CreditsCardData;
  /** Hides the card for this thread. There is no "remind me" — a dismissal is a dismissal. */
  onDismiss: () => void;
}) {
  const { t } = useTranslation();
  const productName = useProductName();
  const { logPageClickBi } = useGenericBi();
  const fraction = creditsCardFraction(card);
  const renewed = card.variant === 'renewed';

  // The credits funnel's widget identity, or null for a card it does not measure (`renewed` — see
  // `creditsCardWidgetType`). Null means no impression AND no `widget_type` on the clicks, so the two
  // halves of the funnel always agree about which cards are in it.
  const widgetType = creditsCardWidgetType(card.variant);
  const widgetFields = widgetType ? { [CREDITS_WIDGET_FIELD]: widgetType } : undefined;
  // The impression (198:6). Attached to the card element so it fires when the card is actually scrolled
  // into view, not when it mounts below the fold in a message list.
  const seenRef = useItemSeenBi('credits-card', { additionalFields: widgetFields, disabled: !widgetType });
  // A card persisted before a promo redemption must not say "running low" while nothing is charged.
  // Gated here, not in the row, so only rows that carry a card subscribe to the balance. Same on both phones.
  const freeUntil = useFreeUsageUntil();

  // The BI button names carry the variant so the two cards are separable in the funnel. Merging them
  // would make a shift in the mix between the cards look like a change in either one's click rate.
  const click = (button: string) => {
    const name = renewed ? `${button}_renewed` : button;
    logPageClickBi({ page_name: 'chat', button_name: name }, { componentName: name, additionalFields: widgetFields });
  };

  const title = renewed ? t('renewed.title') : t('almostOut.title');

  const upgrade = (
    <Button
      fullWidth
      variant={renewed ? 'outlined' : undefined}
      href={upgradeHref('chat_credits_card')}
      onClick={() => click('credits_card_upgrade')}
    >
      {t('almostOut.upgrade')}
    </Button>
  );

  const earn = (
    <Button fullWidth variant={renewed ? undefined : 'outlined'} href="/settings?share=1" onClick={() => click('credits_card_earn')}>
      {t('almostOut.earn')}
    </Button>
  );

  if (freeUntil) return null;

  return (
    <Card component="section" ref={seenRef} className={styles.card} aria-label={title} data-testid="chat-credits-card" data-variant={card.variant}>
      {card.successLine && (
        <p className={styles.success}>
          <Check size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
          <span>{card.successLine}</span>
        </p>
      )}
      <Typography variant="h4" component="h3">
        {title}
      </Typography>
      <Typography variant="p2" component="p" color="secondary">
        {renewed ? t('renewed.body') : t('almostOut.body', { productName })}
      </Typography>

      {/* The same meter the sidebar credits card draws — ink at every level, so this card and the
          bar the user has been watching can never disagree. The card's own words say the allowance is
          running out; the bar only has to say how much is left. */}
      <ProgressBar
        progress="neutral"
        value={Math.round(fraction * 100)}
        label={t('almostOut.remaining', { used: card.used, cap: card.cap })}
        decorative
      />

      <div className={styles.actions}>
        {renewed ? earn : upgrade}
        {renewed ? upgrade : earn}
        <Button
          fullWidth
          variant="text"
          onClick={() => {
            click('credits_card_dismiss');
            onDismiss();
          }}
        >
          {t('almostOut.notNow')}
        </Button>
      </div>
    </Card>
  );
}
